// ============================================================================
// marketDraft.ts — ЧЕРНОВИК ЗАЯВКИ МАРКЕТПЛЕЙСА и НАМЕРЕНИЕ ВОЗВРАТА.
//
// Зачем: гость нажимает «Оставить заявку» → уходит на вход/регистрацию, а
// после входа возвращается В МАСТЕР заявки с уже введёнными данными.
//
// Правила (без сервера: proto/миграции не меняются):
//   • черновик привязан к БАЗЕ (slug) и ХОЗЯИНУ (userId) — чужой черновик
//     не подхватывается; пользователь без id черновик не пишет;
//   • намерение возврата живёт ограниченное время (30 минут) и только в
//     пределах той же базы — «старый» вход не уводит в маркетплейс;
//   • данные только текстовые (категория/адрес/описание/телефон/шаг); фото —
//     это File, они не сериализуются, поэтому в черновик не попадают.
// ============================================================================

import { useCenterStore } from '@/store/centerStore';
import { logSwallowed } from '@/lib/errors';
import { EMPTY_MARKET_GEO, type MarketGeoValue } from '@/lib/marketGeo';

/** Шаг мастера заявки, на котором пользователь остановился. */
export type MarketDraftStep = 1 | 2 | 3 | 4;

export interface MarketDraft {
  /** Владелец черновика: чужой черновик не восстанавливается. */
  userId: string;
  categoryId: string;
  address: string;
  descr: string;
  phone: string;
  /** Гео-схема заявки: страна/регион/город/район, координаты, радиус поиска. */
  geo: MarketGeoValue;
  step: MarketDraftStep;
  /** Время последнего изменения (мс) — показывается пользователю. */
  updatedAt: number;
}

/** Куда вернуть пользователя после входа. */
export type MarketResume = 'wizard' | 'home';

const DRAFT_PREFIX = 'hydra_market_draft:';
const RESUME_PREFIX = 'hydra_market_resume:';
/** Намерение возврата действительно 30 минут (защита от «старых» входов). */
const RESUME_TTL_MS = 30 * 60 * 1000;

function baseKey(): string {
  return useCenterStore.getState().baseSlug || 'local';
}

function draftKey(): string {
  return DRAFT_PREFIX + baseKey();
}

function resumeKey(): string {
  return RESUME_PREFIX + baseKey();
}

function isStep(value: unknown): value is MarketDraftStep {
  return value === 1 || value === 2 || value === 3 || value === 4;
}

function normalizeDraft(raw: unknown, userId: string): MarketDraft | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;
  if (String(value.userId || '') !== userId) return null;
  const draft: MarketDraft = {
    userId,
    categoryId: String(value.categoryId || ''),
    address: String(value.address || ''),
    descr: String(value.descr || ''),
    phone: String(value.phone || ''),
    geo: normalizeDraftGeo(value.geo),
    step: isStep(value.step) ? value.step : 1,
    updatedAt: Number(value.updatedAt) || 0,
  };
  return hasDraftContent(draft) ? draft : null;
}

/** Есть ли в черновике хоть что-то, что стоит восстанавливать. */
export function hasDraftContent(
  draft: Pick<MarketDraft, 'categoryId' | 'address' | 'descr' | 'phone' | 'geo'> | null,
): boolean {
  if (!draft) return false;
  return Boolean(
    draft.categoryId.trim()
    || draft.address.trim()
    || draft.descr.trim()
    || draft.phone.trim()
    || marketGeoDraftHasValue(draft.geo),
  );
}

/** Значение гео в черновике заполнено хотя бы одним полем. */
export function marketGeoDraftHasValue(geo: MarketGeoValue | null | undefined): boolean {
  if (!geo) return false;
  return Boolean(
    geo.countryId.trim()
    || geo.regionId.trim()
    || geo.cityId.trim()
    || geo.areaId.trim()
    || geo.lat.trim()
    || geo.lon.trim()
    || geo.radiusKm.trim(),
  );
}

/** Гео из черновика (старые черновики без гео — пустое значение). */
function normalizeDraftGeo(raw: unknown): MarketGeoValue {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_MARKET_GEO };
  const value = raw as Record<string, unknown>;
  const text = (key: string) => String(value[key] ?? '');
  return {
    countryId: text('countryId'),
    regionId: text('regionId'),
    cityId: text('cityId'),
    areaId: text('areaId'),
    lat: text('lat'),
    lon: text('lon'),
    radiusKm: text('radiusKm'),
  };
}

/** Черновик текущего пользователя (null — нет или он принадлежит другому). */
export function loadMarketDraft(userId: string): MarketDraft | null {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(draftKey());
    if (!raw) return null;
    return normalizeDraft(JSON.parse(raw), userId);
  } catch (e) {
    logSwallowed(e, 'marketDraft: чтение черновика');
    return null;
  }
}

/** Сохранить черновик (пустые данные не пишутся — не засоряем хранилище). */
export function saveMarketDraft(input: Omit<MarketDraft, 'updatedAt'>): void {
  if (!input.userId) return;
  if (!hasDraftContent(input)) return;
  try {
    const draft: MarketDraft = { ...input, updatedAt: Date.now() };
    localStorage.setItem(draftKey(), JSON.stringify(draft));
  } catch (e) {
    logSwallowed(e, 'marketDraft: сохранение черновика');
  }
}

/** Удалить черновик (после успешной отправки или по кнопке «Удалить»). */
export function clearMarketDraft(): void {
  try {
    localStorage.removeItem(draftKey());
  } catch (e) {
    logSwallowed(e, 'marketDraft: удаление черновика');
  }
}

/** Запомнить намерение: после входа вернуться в мастер заявки (или на главную). */
export function setMarketResume(mode: MarketResume): void {
  try {
    localStorage.setItem(resumeKey(), JSON.stringify({
      mode,
      base: baseKey(),
      at: Date.now(),
    }));
  } catch (e) {
    logSwallowed(e, 'marketDraft: сохранение намерения возврата');
  }
}

/** Прочитать намерение, не удаляя его (нужно всем участникам входа). */
export function peekMarketResume(): MarketResume | null {
  try {
    const raw = localStorage.getItem(resumeKey());
    if (!raw) return null;
    const value = JSON.parse(raw) as { mode?: unknown; base?: unknown; at?: unknown };
    if (value.base !== baseKey()) return null;
    if (value.mode !== 'wizard' && value.mode !== 'home') return null;
    const at = Number(value.at) || 0;
    if (!at || Date.now() - at > RESUME_TTL_MS) {
      localStorage.removeItem(resumeKey());
      return null;
    }
    return value.mode;
  } catch (e) {
    logSwallowed(e, 'marketDraft: чтение намерения возврата');
    return null;
  }
}

/** Забрать намерение (одноразово) — вызывается при инициализации MarketApp. */
export function consumeMarketResume(): MarketResume | null {
  const mode = peekMarketResume();
  clearMarketResume();
  return mode;
}

/** Сбросить намерение (например, вход был не из маркетплейса). */
export function clearMarketResume(): void {
  try {
    localStorage.removeItem(resumeKey());
  } catch (e) {
    logSwallowed(e, 'marketDraft: сброс намерения возврата');
  }
}

/** Краткое описание черновика для карточки на главной. */
export function marketDraftSummary(draft: MarketDraft | null, categoryName?: string): string {
  if (!draft) return '';
  const parts: string[] = [];
  if (categoryName) parts.push(categoryName);
  if (draft.address.trim()) parts.push(draft.address.trim());
  if (!parts.length && draft.descr.trim()) parts.push(draft.descr.trim());
  return parts.join(' · ');
}
